import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Minimal structural types for the Web Speech API's SpeechRecognition. The DOM
 * lib does not ship these, and we deliberately avoid pulling an extra @types
 * dependency into this framework-light package, so we model only the surface we
 * touch. A real browser `SpeechRecognition`/`webkitSpeechRecognition` instance
 * structurally satisfies these shapes.
 */
interface SpeechAlternativeLike {
  readonly transcript: string;
}
interface SpeechResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: SpeechAlternativeLike;
}
interface SpeechResultListLike {
  readonly length: number;
  readonly [index: number]: SpeechResultLike;
}
interface SpeechRecognitionEventLike {
  readonly resultIndex: number;
  readonly results: SpeechResultListLike;
}
interface SpeechRecognitionErrorEventLike {
  readonly error: string;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface VoiceDictationOptions {
  /** Called with each finalized transcript segment. */
  onResult: (transcript: string) => void;
  /** BCP-47 language tag. Defaults to the document language or `en-US`. */
  lang?: string;
}

export interface VoiceDictation {
  /** True when the browser exposes the Web Speech API. */
  readonly supported: boolean;
  /** True while a recognition session is active. */
  readonly listening: boolean;
  /** Last recognition error code, or null. */
  readonly error: string | null;
  /** Begin listening (no-op if unsupported or already listening). */
  readonly start: () => void;
  /** Stop listening. */
  readonly stop: () => void;
}

/**
 * React hook wrapping the Web Speech API SpeechRecognition for one-shot voice
 * dictation. Capability-gated: `supported` is false where the API is absent
 * (Firefox, older WebViews, SSR), and `start`/`stop` are safe no-ops there.
 */
export function useVoiceDictation({ onResult, lang }: VoiceDictationOptions): VoiceDictation {
  const [supported] = useState<boolean>(() => getRecognitionCtor() !== null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  // Keep the latest onResult in a ref so start() does not need it as a dep and
  // an in-flight session always calls the current handler.
  const onResultRef = useRef(onResult);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    if (listening) return;
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;

    const recognition = new Ctor();
    const docLang = typeof document !== 'undefined' ? document.documentElement.lang : '';
    recognition.lang = lang ?? (docLang || 'en-US');
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event) => {
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal && result.length > 0) {
          finalText += result[0].transcript;
        }
      }
      const trimmed = finalText.trim();
      if (trimmed) onResultRef.current(trimmed);
    };
    recognition.onerror = (event) => {
      setError(event.error);
      setListening(false);
    };
    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    setError(null);
    setListening(true);
    try {
      recognition.start();
    } catch (err) {
      // Some browsers throw synchronously from start() if a recognition session
      // is already active (Chrome allows only one at a time). onend/onerror
      // never fire for a session that never started, so reset here rather than
      // leaving the UI stuck in the "listening" state.
      recognitionRef.current = null;
      setListening(false);
      setError(err instanceof Error ? err.message : 'speech-start-failed');
    }
  }, [listening, lang]);

  // Abort any in-flight session on unmount so a running mic does not outlive the
  // component.
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  return { supported, listening, error, start, stop };
}
